import { httpGet, httpPost, httpPatch, httpPut, httpDelete } from '../lib/http';

// Centralized logger for API calls and errors
function logApiCall(name, params) {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.info(`[API CALL] ${name}`, params);
  }
}
function logApiError(name, params, error) {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.error(`[API ERROR] ${name}`, params, error);
  }
}

// Helper to wrap API calls with logging and graceful error handling
async function safeApiCall(fn, name, params) {
  logApiCall(name, params);
  try {
    const result = await fn();
    return result;
  } catch (error) {
    logApiError(name, params, error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
}

// Helper to check response status and parse JSON
async function parseResponse(res, errorContext = '') {
  if (!res || !res.ok) {
    throw new Error(`${errorContext}: ${res?.status || 'unknown error'}`);
  }
  return await res.json();
}

// Simple in-flight request dedupe map to avoid duplicate network calls
const inFlightRequests = new Map();


// Categories & Exams
export const getCategories = async () => safeApiCall(async () => {
  const res = await httpGet('/api/indian-exams/categories');
  return await parseResponse(res, 'Failed to fetch categories');
}, 'getCategories');
export const getExamsByCategory = async (categoryId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/categories/${encodeURIComponent(categoryId)}/exams`);
  return await parseResponse(res, 'Failed to fetch exams');
}, 'getExamsByCategory', { categoryId });
export const listExams = async (page = 1, limit = 20) => {
  const key = `listExams:${page}:${limit}`;
  if (inFlightRequests.has(key)) return inFlightRequests.get(key);

  const promise = (async () => {
    const result = await safeApiCall(async () => {
      const res = await httpGet(`/api/indian-exams/exams?page=${page}&limit=${limit}`);
      return await parseResponse(res, 'Failed to list exams');
    }, 'listExams', { page, limit });
    return result;
  })();

  inFlightRequests.set(key, promise);
  try {
    return await promise;
  } finally {
    inFlightRequests.delete(key);
  }
};
export const getExamById = async (examId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/exams/${encodeURIComponent(examId)}`);
  return await parseResponse(res, 'Failed to fetch exam');
}, 'getExamById', { examId });
export const updateExam = async (examId, payload) => safeApiCall(async () => {
  const res = await httpPut(`/api/indian-exams/exams/${encodeURIComponent(examId)}`, payload);
  return await parseResponse(res, 'Failed to update exam');
}, 'updateExam', { examId, payload });
export const deleteExam = async (examId) => safeApiCall(async () => {
  const res = await httpDelete(`/api/indian-exams/exams/${encodeURIComponent(examId)}`);
  return await parseResponse(res, 'Failed to delete exam');
}, 'deleteExam', { examId });
export const searchExams = async (keyword) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/search?keyword=${encodeURIComponent(keyword)}`);
  return await parseResponse(res, 'Failed to search exams');
}, 'searchExams', { keyword });
export const getRecommendations = async (userCountry) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/recommendations?userCountry=${encodeURIComponent(userCountry)}`);
  return await parseResponse(res, 'Failed to get recommendations');
}, 'getRecommendations', { userCountry });
export const getExamStats = async () => safeApiCall(async () => {
  const res = await httpGet('/api/indian-exams/stats');
  return await parseResponse(res, 'Failed to fetch stats');
}, 'getExamStats');


// Exam Progress
export const getExamProgress = async (examId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/progress${examId ? `?examId=${encodeURIComponent(examId)}` : ''}`);
  return await parseResponse(res, 'Failed to fetch exam progress');
}, 'getExamProgress', { examId });
export const updateExamProgress = async (examId, progressData) => safeApiCall(async () => {
  const res = await httpPost('/api/indian-exams/progress', { examId, progressData });
  return await parseResponse(res, 'Failed to update exam progress');
}, 'updateExamProgress', { examId, progressData });


// Papers
export const listPapers = async (examId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/exams/${encodeURIComponent(examId)}/papers`);
  return await parseResponse(res, 'Failed to list papers');
}, 'listPapers', { examId });

// Alias for listPapers - used in AttemptExamModal
export const getExamPapers = listPapers;

export const getPaperContent = async (examId, paperId, params) => safeApiCall(async () => {
  const qs = new URLSearchParams(params || {}).toString();
  const path = `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}/content` + (qs ? `?${qs}` : '');
  const  res = await httpGet(path);
  return await parseResponse(res, 'Failed to fetch paper content');
}, 'getPaperContent', { examId, paperId, params });
export const createPaper = async (examId, metadata, questions, chunkSize) => safeApiCall(async () => {
  const res = await httpPost(`/api/indian-exams/exams/${encodeURIComponent(examId)}/papers`, { metadata, questions, chunkSize });
  return await parseResponse(res, 'Failed to create paper');
}, 'createPaper', { examId, metadata, questions, chunkSize });
export const updatePaper = async (examId, paperId, metadata) => safeApiCall(async () => {
  const res = await httpPut(`/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}`, { metadata });
  return await parseResponse(res, 'Failed to update paper');
}, 'updatePaper', { examId, paperId, metadata });
export const setPaperActive = async (examId, paperId, isActive) => safeApiCall(async () => {
  const res = await httpPatch(`/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}/active`, { isActive });
  return await parseResponse(res, 'Failed to set paper active');
}, 'setPaperActive', { examId, paperId, isActive });
export const deletePaper = async (examId, paperId) => safeApiCall(async () => {
  const res = await httpDelete(`/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}`);
  return await parseResponse(res, 'Failed to delete paper');
}, 'deletePaper', { examId, paperId });
export const getExamAggregates = async (examId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/exams/${encodeURIComponent(examId)}/aggregates`);
  return await parseResponse(res, 'Failed to fetch exam aggregates');
}, 'getExamAggregates', { examId });
export const recomputeExamAggregates = async (examId) => safeApiCall(async () => {
  const res = await httpPost(`/api/indian-exams/exams/${encodeURIComponent(examId)}/aggregates/recompute`);
  return await parseResponse(res, 'Failed to recompute aggregates');
}, 'recomputeExamAggregates', { examId });
export const searchPapers = async (params) => safeApiCall(async () => {
  const qs = new URLSearchParams(params || {}).toString();
  const res = await httpGet(`/api/indian-exams/papers/search${qs ? `?${qs}` : ''}`);
  return await parseResponse(res, 'Failed to search papers');
}, 'searchPapers', { params });
export const getPapersContent = async (params) => safeApiCall(async () => {
  const qs = new URLSearchParams(params || {}).toString();
  const res = await httpGet(`/api/indian-exams/papers/content${qs ? `?${qs}` : ''}`);
  return await parseResponse(res, 'Failed to fetch papers content');
}, 'getPapersContent', { params });

// DI (Data Interpretation) Enhanced Endpoints
export const searchPapersDI = async (params) => safeApiCall(async () => {
  const qs = new URLSearchParams(params || {}).toString();
  const res = await httpGet(`/api/indian-exams/papers/search-di${qs ? `?${qs}` : ''}`);
  return await parseResponse(res, 'Failed to search papers (DI)');
}, 'searchPapersDI', { params });
export const getPaperContentDI = async (examId, paperId, options = {}) => safeApiCall(async () => {
  const params = new URLSearchParams();
  if (options.includePassages) params.append('includePassages', 'true');
  if (options.imageResolution) params.append('imageResolution', options.imageResolution);
  if (options.maxChunks) params.append('maxChunks', options.maxChunks);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await httpGet(`/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}/content-di${qs}`);
  return await parseResponse(res, 'Failed to fetch paper content (DI)');
}, 'getPaperContentDI', { examId, paperId, options });
export const getPaperImages = async (paperId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/papers/${encodeURIComponent(paperId)}/images`);
  return await parseResponse(res, 'Failed to fetch paper images');
}, 'getPaperImages', { paperId });


// Attempts & Leaderboard
export const createAttempt = async (examId) => safeApiCall(async () => {
  const res = await httpPost('/api/indian-exams/attempts', { examId });
  return await parseResponse(res, 'Failed to create attempt');
}, 'createAttempt', { examId });
export const completeAttempt = async (attemptId, score) => safeApiCall(async () => {
  const res = await httpPost(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}/complete`, { score });
  return await parseResponse(res, 'Failed to complete attempt');
}, 'completeAttempt', { attemptId, score });
export const listAttempts = async () => safeApiCall(async () => {
  const res = await httpGet('/api/indian-exams/attempts');
  return await parseResponse(res, 'Failed to list attempts');
}, 'listAttempts');
export const getAttemptById = async (attemptId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/attempts/${encodeURIComponent(attemptId)}`);
  return await parseResponse(res, 'Failed to fetch attempt');
}, 'getAttemptById', { attemptId });
export const getLeaderboard = async (examId) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/exams/${encodeURIComponent(examId)}/leaderboard`);
  return await parseResponse(res, 'Failed to fetch leaderboard');
}, 'getLeaderboard', { examId });


// Topic Progress
export const upsertTopicProgress = async (topic, progress) => safeApiCall(async () => {
  const res = await httpPost('/api/indian-exams/topic-progress', { topic, progress });
  return await parseResponse(res, 'Failed to update topic progress');
}, 'upsertTopicProgress', { topic, progress });
export const getTopicProgress = async () => safeApiCall(async () => {
  const res = await httpGet('/api/indian-exams/topic-progress');
  return await parseResponse(res, 'Failed to fetch topic progress');
}, 'getTopicProgress');


// Ingestion (Admin)
export const ingestPreviewPaste = async (examId, language, content, options) => safeApiCall(async () => {
  const res = await httpPost('/api/indian-exams/ingest/preview/paste', { examId, language, content, options });
  return await parseResponse(res, 'Failed to preview paste');
}, 'ingestPreviewPaste', { examId, language, options });
export const ingestPreviewUpload = async (examId, language, filename, content, options) => safeApiCall(async () => {
  const res = await httpPost('/api/indian-exams/ingest/preview/upload', { examId, language, filename, content, options });
  return await parseResponse(res, 'Failed to preview upload');
}, 'ingestPreviewUpload', { examId, language, filename, options });
export const readIngestPreview = async (sessionId, chunkIndex = 0) => safeApiCall(async () => {
  const res = await httpGet(`/api/indian-exams/ingest/preview/${encodeURIComponent(sessionId)}?chunkIndex=${chunkIndex}`);
  return await parseResponse(res, 'Failed to read ingest preview');
}, 'readIngestPreview', { sessionId, chunkIndex });
